import type { FAQItem } from "@/types/content";

export const homepageHero = {
  eyebrow: "The Wealth Aangan",
  title: "Where wealth comes home.",
  subtitle: "Reviewed with structure. Held with care.",
  primaryCta: "Request a Wealth Review",
  secondaryCta: "See How We Review",
  microcopy:
    "The first step is a review conversation. No product action is taken from this form.",
} as const;

export const homepageQuietClutter = {
  eyebrow: "Quiet Clutter",
  title: "Money waits quietly.",
  lines: ["In accounts.", "In files.", "In thoughts."] as const,
  fragments: [
    "Portfolio statement edge",
    "Tax note corner",
    "Passport hint",
    "Business ledger line",
    "Education cue",
    "Retirement date",
    "Nomination document",
    "Family photo outline",
    "Home key shadow",
    "Liquidity marker",
  ] as const,
} as const;

export const homepageCalmCentre = {
  eyebrow: "The Aangan",
  title: "Bring it to one calm place.",
  chips: ["Goals", "Risks", "Family", "Future"] as const,
} as const;

export const homepageRoomsOfCapital = {
  eyebrow: "Strategic Solutions",
  title: "Strategic solutions by Bharat's trusted wealth management firm.",
  subtitle: "Every product plays a distinct role.",
} as const;

export type HomepageProductSolution = {
  name: string;
  features: readonly { title: string; copy: string }[];
  note: string;
  /** Destination for the "Learn More" CTA. Omit if no dedicated page exists yet. */
  href?: string;
};

export const homepageProductSolutions: readonly HomepageProductSolution[] = [
  {
    name: "Portfolio Management Services (PMS)",
    features: [
      { title: "Manager-Led Portfolios", copy: "Strategy driven by a distinct investment philosophy and portfolio construction approach." },
      { title: "Flexible Across Asset Classes", copy: "Equity, debt, and multi-asset strategies aligned to your risk, return expectations, and goals." },
    ],
    note: "For portfolios starting ₹50L+",
    href: "/pms-portfolio-management-services",
  },
  {
    name: "Alternative Investment Funds (AIFs)",
    features: [
      { title: "Private Market Opportunities (Cat I, II & III)", copy: "Exposure to private equity, venture, credit, and special situations." },
      { title: "Beyond Listed Markets", copy: "Participation in opportunities not available in traditional public markets." },
    ],
    note: "For investments starting ₹1Cr+",
    href: "/aif-alternative-investment-funds",
  },
  {
    name: "Mutual Funds",
    features: [
      { title: "Core Portfolio Building", copy: "Diversified exposure across equity, debt, and hybrid categories." },
      { title: "Portfolio Stability & Flexibility", copy: "Helps balance portfolios while adapting to changing market conditions." },
    ],
    note: "Accessible across investment sizes.",
    href: "/mutual-funds-advisory",
  },
  {
    name: "Bonds / Fixed Income",
    features: [
      { title: "Income and Stability", copy: "Government and corporate issuances across credit profiles." },
      { title: "Interest Rate Positioning", copy: "Portfolios aligned to changing rate cycles and yield movements." },
    ],
    note: "Used for income generation and capital stability.",
    href: "/bonds-investment",
  },
  {
    name: "Market Linked Debentures (MLDs)",
    features: [
      { title: "Structured Market Participation", copy: "Returns linked to equity indices through predefined structures." },
      { title: "Defined Payoff Frameworks", copy: "Different outcomes based on market scenarios, including capital protection structures in select cases." },
    ],
    note: "An alternative to direct equity exposure in certain conditions.",
    href: "/mld-market-linked-debentures",
  },
  {
    name: "Corporate Fixed Deposits",
    features: [
      { title: "Fixed Tenure Instruments", copy: "Corporate-issued deposits across varying durations." },
      { title: "Yield Differentials", copy: "Typically offer slightly higher yields compared to traditional bank deposits." },
    ],
    note: "Used for predictable income allocation.",
    href: "/corporate-fixed-deposits",
  },
  {
    name: "GIFT City Investments",
    features: [
      { title: "Global Investment Access", copy: "Exposure to international markets through regulated offshore structures." },
      { title: "Currency Diversification", copy: "Investments beyond INR-based assets." },
    ],
    note: "Useful for global portfolio diversification.",
  },
  {
    name: "Special Investment Funds (SIFs)",
    features: [
      { title: "New-Age Investment Structures", copy: "Designed under an evolving regulatory framework with flexible mandates." },
      { title: "Strategy Diversity", copy: "Includes long-short, ex-top 100, hybrid, and debt-oriented approaches." },
      { title: "Dynamic Positioning", copy: "Ability to adapt across market conditions rather than staying fully directional." },
    ],
    note: "For investors exploring beyond traditional mutual fund strategies.",
    href: "/sif-specialised-investment-funds",
  },
  {
    name: "National Pension System (NPS)",
    features: [
      { title: "Retirement-Oriented Investing", copy: "Exposure across equity, debt, and government securities." },
      { title: "Long-Term Compounding", copy: "Structured approach for retirement accumulation." },
    ],
    note: "Designed for disciplined, long-horizon investing.",
    href: "/nps-national-pension-system",
  },
  {
    name: "Insurance (Health & Term)",
    features: [
      { title: "Risk Coverage", copy: "Protection against health and life uncertainties." },
      { title: "Financial Continuity", copy: "Helps safeguard long-term financial plans." },
    ],
    note: "Complements the overall financial structure.",
  },
] as const;

export type HomepageStrategicSolution = {
  name: string;
  description: string;
  /** Destination for the "Learn More" CTA. Omit if no dedicated page exists yet. */
  href?: string;
};

export const homepageStrategicSolutions: readonly HomepageStrategicSolution[] = [
  {
    name: "Portfolio Management Services (PMS)",
    description:
      "Professionally managed portfolios with distinct investment strategies across equity, debt, and multi-asset solutions.",
    href: "/pms-portfolio-management-services",
  },
  {
    name: "Mutual Funds",
    description:
      "Diversified investment options across equity, debt, and hybrid funds to help build long-term portfolios for every investor.",
    href: "/mutual-funds-advisory",
  },
  {
    name: "Non-Convertible Debentures (NCDs)",
    description: "Corporate debt instruments that offer fixed interest over a defined investment tenure.",
    href: "/ncd-non-convertible-debentures",
  },
  {
    name: "Alternative Investment Funds (AIFs)",
    description:
      "Access private market opportunities, including private equity, private credit, and alternative strategies beyond listed markets.",
    href: "/aif-alternative-investment-funds",
  },
  {
    name: "Fixed Income",
    description:
      "Debt investment solutions focused on generating regular income while adding stability to investment portfolios.",
    href: "/fixed-income-investments",
  },
  {
    name: "Bonds",
    description: "Invest across government and corporate issuances with varying maturities and credit profiles.",
    href: "/bonds-investment",
  },
  {
    name: "Market Linked Debentures (MLDs)",
    description:
      "Structured investments with returns linked to market performance through predefined payoff structures.",
    href: "/mld-market-linked-debentures",
  },
  {
    name: "Corporate Fixed Deposits",
    description:
      "Corporate-issued fixed deposits offering competitive interest rates across multiple investment tenures.",
    href: "/corporate-fixed-deposits",
  },
  {
    name: "Special Investment Funds (SIFs)",
    description:
      "Innovative investment strategies with greater flexibility across equity, debt, and hybrid mandates under the SIF framework.",
    href: "/sif-specialised-investment-funds",
  },
  {
    name: "National Pension System (NPS)",
    description:
      "A long-term investment solution that builds retirement wealth through disciplined investing across multiple asset classes.",
    href: "/nps-national-pension-system",
  },
] as const;

export const homepageRoomLines = [
  { name: "Growth", copy: "Capital that works for tomorrow." },
  { name: "Liquidity", copy: "Money kept ready for life." },
  { name: "Income", copy: "Cash flow with context." },
  { name: "Protection", copy: "Comfort that should not be left exposed." },
  { name: "Retirement", copy: "The future made less abstract." },
  { name: "Legacy", copy: "Intent arranged before it becomes urgent." },
  { name: "Global", copy: "Wider exposure, reviewed with care." },
  { name: "Business", copy: "Personal and business wealth, seen separately." },
  { name: "Review Rhythm", copy: "Because wealth changes when life does." },
] as const;

export const homepageInteractiveAangan = {
  eyebrow: "Interactive Wealth Aangan",
  title: "Explore the role before the product.",
  selectedLabel: "Selected room",
} as const;

export const homepageFoundation = {
  eyebrow: "Choice Group Foundation",
  title: "Modern in experience.",
  subtitle: "Institutional in foundation.",
  body: "Choice Wealth from the Choice Group ecosystem.",
  proofs: [
    "₹4,200+ Cr Wealth AUM [TO VALIDATE]",
    "15+ Years of Expertise",
    "190+ Pan-India Branch Offices",
  ] as const,
} as const;

export const homepageHumanTechnology = {
  eyebrow: "Human + Technology",
  title: "Technology sees the signals.",
  subtitle: "People understand the story.",
  signals: [
    "Review date",
    "Liquidity cue",
    "Concentration cue",
    "Documentation note",
    "Risk marker",
    "Product exposure cue",
  ] as const,
} as const;

export const homepageReviewRhythm = {
  eyebrow: "Review Rhythm",
  title: "The real luxury is not more products.",
  subtitle: "Because portfolios should evolve with life, not just with markets.",
  checkpoints: [
    "Review date",
    "Life milestone",
    "Liquidity need",
    "Family cue",
    "Tax context",
    "Market context",
  ] as const,
} as const;

export const homepageStartingPointSection = {
  eyebrow: "Starting Point Selector",
  title: "Start where your mind is already busy.",
  prompt: "What does your wealth need reviewed?",
  helperTitle: "Find My Review Starting Point",
  helperSummary: "A Wealth Review can help route this conversation.",
  helperFallback: "Good place to begin.",
  helperCta: "Request a Wealth Review",
} as const;

export const homepageStartingPoints = [
  "Portfolio structure",
  "Risk and liquidity",
  "Income and cash flow",
  "Retirement readiness",
  "Legacy or family continuity",
  "India-linked NRI wealth",
  "Business-linked wealth",
  "Global investment context",
  "Not sure yet",
] as const;

export const homepageSimpleTerms = {
  eyebrow: "In Simple Terms",
  body: "Choice Wealth helps serious investors review wealth across goals, risk, liquidity, income needs, family context and product exposure before discussing relevant investment solutions or product access.",
} as const;

export const homepageFinalFrame = {
  eyebrow: "Final Frame",
  title: "Where wealth comes home.",
  subtitle: "Reviewed with structure. Held with care.",
  cta: "Request a Wealth Review",
  microcopy:
    "Reviewed first. Product access only after context, eligibility and clear discussion.",
} as const;

export const homepageRangeOptions = [
  "Below ₹25L",
  "₹25L to ₹50L",
  "₹50L to ₹1Cr",
  "₹1Cr to ₹5Cr",
  "₹5Cr to ₹10Cr",
  "₹10Cr+",
  "Prefer to discuss",
] as const;

export const homepageContactTimeOptions = [
  "Morning",
  "Afternoon",
  "Evening",
  "Specific slot",
] as const;

export const homepageFaqHeading = {
  eyebrow: "Questions Before the Door Closes",
  title: "Questions answered calmly.",
} as const;

export const homepageFaqs: readonly FAQItem[] = [
  {
    question: "What does Choice Wealth do?",
    answer:
      "Choice Wealth helps serious investors begin with a structured wealth review. The conversation may cover goals, risk, liquidity, income needs, family context, product exposure and relevant investment solutions. Product access is discussed only after context, eligibility, documentation and applicable risks are reviewed.",
  },
  {
    question: "What is a Wealth Review?",
    answer:
      "A Wealth Review is an RM-led conversation to understand what your capital is meant to do, where it currently sits and what may need attention. It is not an automatic product action. The first step is to review context before discussing possible next steps.",
  },
  {
    question: "Is the first conversation a product pitch?",
    answer:
      "No. The first conversation is meant to understand your wealth context. Product access, where relevant, is discussed only after your needs, eligibility, documentation, product terms and applicable risks are considered.",
  },
  {
    question: "Who is Choice Wealth suitable for?",
    answer:
      "Choice Wealth is designed for serious investors, families, NRIs, business owners, senior professionals and clients who want a more structured conversation around capital, risk, liquidity, income, retirement, legacy or product exposure.",
  },
  {
    question: "What happens after I submit the form?",
    answer:
      "Your details help route your enquiry to the relevant Choice Wealth desk. The team reviews your context and connects with you for a structured conversation. No product action is taken from the form submission.",
  },
  {
    question: "Does Choice Wealth offer product access?",
    answer:
      "Choice Wealth may help clients access relevant investment solutions, subject to eligibility, documentation, product terms, applicable risks and regulations. The conversation begins with review, not product selection.",
  },
  {
    question: "Is any product action taken automatically?",
    answer:
      "No. No product action is taken automatically. Any product-related step requires client understanding, confirmation and completion of the required process.",
  },
  {
    question: "How is Choice Wealth connected to Choice Group?",
    answer:
      "Choice Wealth is part of the wider Choice Group ecosystem. Final entity wording and verified group proof should be used as legally approved.",
  },
] as const;

export const homepageFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: homepageFaqs.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: typeof item.answer === "string" ? item.answer : "",
    },
  })),
} as const;

export const homepageFooterGroups = [
  {
    title: "Explore",
    links: [
      { label: "Who We Serve", href: "/who-we-serve" },
      { label: "Services", href: "/services" },
      { label: "Products", href: "/products" },
    ],
  },
  {
    title: "Insight",
    links: [
      { label: "Insights", href: "/blog" },
      { label: "Contact", href: "/contact-us" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms & Conditions", href: "/terms-and-conditions" },
    ],
  },
] as const;

export const homepageFooterCaveat =
  "Investment products are subject to market, credit, liquidity and other applicable risks. Product access is subject to eligibility, documentation, product terms and applicable regulations. Tax, legal, estate and succession matters should be reviewed with qualified professionals.";
